import { PRODUCTS } from '@constants/global';
import useProductVersions from '@hooks/useProductVersions';
import useCanonical from '@hooks/useCanonical';

type SEOProps = {
  pageContext?: PageContext;
  title: string;
  description?: string;
  shouldNotIndex?: boolean;
  canonicalPageCodename?: string;
};

const SEO = ({
  pageContext,
  title,
  description,
  canonicalPageCodename,
  shouldNotIndex,
}: SEOProps) => {
  const productCodename = pageContext?.product?.system.codename;
  const { getVersionById, latestVersionId } = useProductVersions(productCodename);
  const currentVersionId = pageContext?.versionId;
  const currentVersion = currentVersionId && getVersionById(currentVersionId);
  const isLatestVersion =
    productCodename === PRODUCTS.sonarqube && latestVersionId === currentVersionId;
  const hasCanonicalToLatestVersion = Boolean(currentVersion && isLatestVersion);

  const { canonicalUrl } = useCanonical({
    canonicalPageCodename,
    hasCanonicalToLatestVersion,
    urlSlug: currentVersion && currentVersion?.urlSlug,
    baseUrl: pageContext?.baseUrl,
  });

  return (
    <>
      <meta lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="apple-mobile-web-app-title" content="Sonar Docs" />
      {getFaviconUrls(productCodename)}
      <link rel="apple-touch-icon" href="app-icon.png" />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {shouldNotIndex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageContext?.baseUrl || '/'} />
      <meta name="zd-site-verification" content="8v98tjqiia64uiirbcajv3" />
    </>
  );
};

function getFaviconUrls(productCodename?: string) {
  const baseFaviconUrl = productCodename ? `/${productCodename}/favicon` : '/favicon';
  return [
    <link key="favicon-ico" rel="icon" href={`${baseFaviconUrl}.ico`} />,
    <link
      key="favicon-32x32"
      rel="icon"
      type="image/png"
      sizes="32x32"
      href={`${baseFaviconUrl}-32x32.png`}
    />,
  ];
}

export default SEO;
